import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';

export type CalculatePaymentsResult = {
  collectors_total: number;
  payments_rolled_back: number;
  payments_created: number;
  below_threshold: number;
  no_pending: number;
  errored: number;
  details: Array<{
    collector_id: string;
    rolled_back?: number;
    weeks_recrystallized?: number;
    result?: 'created' | 'below_threshold' | 'no_pending' | 'already_exists';
    amount?: number;
    error?: string;
  }>;
};

function buildSummary(r: CalculatePaymentsResult): string {
  const parts: string[] = [];
  parts.push(`${r.payments_created} pago(s) creado(s)`);
  if (r.payments_rolled_back > 0) {
    parts.push(`${r.payments_rolled_back} recalculado(s)`);
  }
  if (r.below_threshold > 0) {
    parts.push(`${r.below_threshold} con menos de $3`);
  }
  if (r.errored > 0) {
    parts.push(`${r.errored} con error`);
  }
  return parts.join(' • ');
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: ['payments-pending'] });
  qc.invalidateQueries({ queryKey: ['payments-paid'] });
  qc.invalidateQueries({ queryKey: ['payments-history'] });
  qc.invalidateQueries({ queryKey: ['collector-summary'] });
  qc.invalidateQueries({ queryKey: ['earnings-daily'] });
  qc.invalidateQueries({ queryKey: ['earnings-ranking'] });
  qc.invalidateQueries({ queryKey: ['bonus-history'] });
}

export function useCalculatePayments() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (): Promise<CalculatePaymentsResult> =>
      pb.send<CalculatePaymentsResult>('/api/payments/calculate', {
        method: 'POST',
      }),
    onSuccess: (r) => {
      invalidateAll(qc);
      toast(buildSummary(r), r.errored > 0 ? 'error' : 'success');
    },
    onError: (err) => toast(`Error al calcular: ${String(err)}`, 'error'),
  });
}
